<script setup lang="ts">
import { computed, reactive, ref, watch } from 'vue';

import { CALL_SIGN_MAX, CALL_SIGN_MIN, isWellFormedCallSign } from '@shared/call-sign';
import { COUNTRIES } from '@shared/countries';

import { api, ApiError, type BillingAddressDto } from '@/net/api';
import { STATE_REQUIRED_COUNTRIES, validate as validateBilling, type BillingAddress as CoreBilling } from '@/core/payments/billing-address';

import { formatDate } from './format';
import { rankArtPath } from '@/garage/rank-ladder';
import { useAccount } from '@/state/account';
import { useGarage } from '@/state/garage';
import { useSession } from '@/state/session';

/**
 * Account: the name on an invoice, the address, the two preferences, the password
 * and the way out.
 *
 * **Nothing here computes anything.** Every value is the server's — the profile,
 * the preferences, and the two option lists from `GET /api/i18n/config`. The
 * dropdowns in particular hold no copy of the enabled locales or currencies: a
 * language added to `LOCALES` appears here by existing and one removed vanishes,
 * which is the rule the research branches and the rank ladder already follow.
 */
const account = useAccount();
const session = useSession();
const garage = useGarage();

const emit = defineEmits<{ deleted: [] }>();

// ─── The card ────────────────────────────────────────────────────────────────

/**
 * The rank comes from the garage's overview, not from `/api/account`.
 *
 * `AccountProfile` carries the email, the names and the enlistment date, and
 * adding a rank to it would put the ladder's current standing in two places that
 * are fetched at different times — the garage already refreshes its copy on every
 * purchase and every battle. The card is the only thing here that wants it, and
 * it degrades: with no overview loaded there is no badge and no rank name, which
 * is what a deep link straight to this pane gets.
 */
const wallet = computed(() => garage.wallet.value);

/**
 * The missing-file case is the component's, exactly as it is on the garage's own
 * badge — a path derived from a number cannot know whether the file is there, so
 * the `<img>` has to try and say. The plate underneath carries the number, so a
 * rank with no art is a rank with no picture rather than a broken image.
 */
const rankArtMissing = ref(false);
const rankArt = computed(() => {
  const rank = wallet.value?.rank;
  if (!rank || rankArtMissing.value) return null;
  return rankArtPath(rank);
});
watch(
  () => wallet.value?.rank,
  () => {
    rankArtMissing.value = false;
  },
);

/**
 * The card is the display *and* the edit, in one place and one mode at a time.
 *
 * It used to be a display over two rows that wrote it, and each line focused the
 * input below rather than becoming one — on the argument that the call sign
 * carries a shape rule, a uniqueness refusal and a verification gate, and a
 * second place to type it would be a second place to explain all three. That
 * argument was sound about *duplication* and wrong about which copy to keep: the
 * fix is one place to type, not two places with the typing kept away from the
 * card. All three explanations moved up with the inputs, so they are still stated
 * exactly once.
 *
 * What it buys is a screen with nothing on it to fiddle with. At rest the card is
 * text — no boxes, no Save beside a field nobody is editing, and no control on a
 * name that is already correct, which is most of the time somebody is looking at
 * this. Editing is a thing you ask for.
 */
const editing = ref(false);

/**
 * Both names or neither, because the gate is the same fact about the account.
 *
 * `nameEditable` and `callSignEditable` are separate computeds that both read
 * `account.verified` — kept apart because they are separate server routes with
 * separate refusals, not because they can currently disagree. If one ever does,
 * this is the line that has to grow a per-field disable rather than the button
 * quietly editing a field the server will refuse.
 */
const cardEditable = computed(() => nameEditable.value && callSignEditable.value);

const cardDirty = computed(() => nameDirty.value || callSignDirty.value);

/**
 * Save is dead until something changed *and* what changed can be accepted.
 *
 * The call sign's shape is the only client-side judgement here, and it gates only
 * itself: a well-formed name with a malformed call sign must not be held hostage,
 * so the check is `callSignDirty && !wellFormed` rather than `!wellFormed`.
 */
const canSaveCard = computed(
  () =>
    cardEditable.value &&
    cardDirty.value &&
    !(callSignDirty.value && !callSignWellFormed.value) &&
    !account.busy.value,
);

function beginEdit(): void {
  if (!cardEditable.value) return;
  editing.value = true;
}

/**
 * Leaving edit mode has to put the server's answers back.
 *
 * The two refs are seeded by watchers on the profile, and a watcher does not
 * fire when nothing on the server changed — so abandoning an edit without this
 * would leave the typed value sitting in the ref, and the card would go back to
 * *displaying* a name the account does not have.
 */
function cancelEdit(): void {
  fullName.value = account.profile.value?.fullName ?? '';
  callSign.value = account.profile.value?.callSign ?? '';
  editing.value = false;
}

/**
 * One button, two requests, and it only sends the ones that changed.
 *
 * Sequential rather than concurrent: both write the same profile and the store
 * re-reads it after each, so firing them together would race two refreshes and
 * leave whichever landed second as the card's idea of both fields. Edit mode
 * closes only if every request it made succeeded — a rejected call sign (taken,
 * reserved, refused) has to leave the box open with the text still in it, which
 * is the whole reason the uniqueness answer cannot be predicted here.
 */
async function saveCard(): Promise<void> {
  if (!canSaveCard.value) return;
  if (nameDirty.value && !(await account.saveFullName(fullName.value))) return;
  if (callSignDirty.value && !(await account.saveCallSign(callSign.value.trim()))) return;
  editing.value = false;
}

// ─── Full name ───────────────────────────────────────────────────────────────

const fullName = ref('');
/**
 * Seeded from the server, and re-seeded whenever the server's answer changes so a
 * save is reflected — but not bound two-way onto the store, which would write on
 * every keystroke and make the Save button meaningless.
 */
watch(
  () => account.profile.value?.fullName,
  (value) => {
    fullName.value = value ?? '';
  },
  { immediate: true },
);

const nameDirty = computed(
  () => fullName.value.trim() !== (account.profile.value?.fullName ?? ''),
);

/**
 * The one field an unconfirmed account cannot edit, and the reason is structural
 * rather than a policy: the row it writes to is created by the garage's first
 * read, and the garage is behind the verification gate. It costs nothing — an
 * unverified account cannot buy anything, so it has no invoice to put a name on —
 * and saying so beats offering an edit that 404s. See
 * `AccountService.updateFullName`.
 */
const nameEditable = computed(() => account.verified.value);

// ─── Call sign ───────────────────────────────────────────────────────────────

/**
 * Seeded and re-seeded exactly as the full name is, and for the same reason: a
 * two-way bind onto the store would write on every keystroke, and here that
 * would also mean the confirmation line disagreed with the box while it was
 * being typed in.
 */
const callSign = ref('');
watch(
  () => account.profile.value?.callSign,
  (value) => {
    callSign.value = value ?? '';
  },
  { immediate: true },
);

/**
 * The shape rule is the server's own, imported rather than restated — see
 * `shared/call-sign.ts`. It exists on this side only so the Save button is dead
 * while the box holds something that cannot possibly be accepted, which is the
 * same courtesy the delete button pays and carries the same disclaimer: the
 * server refuses independently, and its refusal is the one that counts.
 *
 * It deliberately says nothing about uniqueness or about the reserved names.
 * Those are facts about the server's database; a client that pretended to know
 * them would be wrong the moment somebody else took a name, and the honest answer
 * to "is this one free" is a request.
 */
const callSignDirty = computed(
  () => callSign.value.trim() !== (account.profile.value?.callSign ?? ''),
);

const callSignWellFormed = computed(() => isWellFormedCallSign(callSign.value.trim()));

/**
 * Same gate as the invoice name, and for the same structural reason rather than
 * a policy one: the row this writes to is created by the garage's first read,
 * and the garage is behind the verification gate. There is nothing to rename
 * until there is a profile.
 */
const callSignEditable = computed(() => account.verified.value);


// ─── Billing address ─────────────────────────────────────────────────────────

/**
 * The core billing identity (t-game-core payments): sent to the payment gateway
 * with every payment whenever it is complete, required for Apple Pay / Open
 * Banking, and frozen onto each invoice when it is issued. Validated with the
 * gateway's own limits (shared with the server), saved with
 * `PUT /api/payments/billing-address`.
 */
const EMPTY_BILLING: BillingAddressDto = {
  firstName: null, lastName: null, line1: null, line2: null, city: null, zip: null, state: null, country: null,
};
const address = reactive<BillingAddressDto>({ ...EMPTY_BILLING });
const savedAddress = ref<BillingAddressDto>({ ...EMPTY_BILLING });
const billingComplete = ref(false);
const billingBusy = ref(false);
const billingError = ref<string | null>(null);

async function loadBilling(): Promise<void> {
  try {
    const view = await api.billingIdentity();
    savedAddress.value = { ...EMPTY_BILLING, ...view.address };
    Object.assign(address, savedAddress.value);
    billingComplete.value = view.complete;
  } catch {
    /* not signed in yet / preview build — the form stays empty */
  }
}
void loadBilling();

const needsState = computed(() => STATE_REQUIRED_COUNTRIES.includes((address.country ?? '').toUpperCase()));

const billingErrors = computed(() => validateBilling(address as unknown as CoreBilling) as Record<string, string | undefined>);

const addressDirty = computed(() => {
  const same = (x: string | null, y: string | null) => (x ?? '').trim() === (y ?? '').trim();
  return (Object.keys(EMPTY_BILLING) as Array<keyof BillingAddressDto>).some(
    (k) => !same(address[k], savedAddress.value[k]),
  );
});

async function saveBilling(): Promise<void> {
  billingBusy.value = true;
  billingError.value = null;
  try {
    const body: Partial<BillingAddressDto> = {};
    for (const k of Object.keys(EMPTY_BILLING) as Array<keyof BillingAddressDto>) {
      const v = (address[k] ?? '').trim();
      body[k] = v.length ? (k === 'country' || k === 'state' ? v.toUpperCase() : v) : null;
    }
    if (!needsState.value) body.state = null;
    const view = await api.saveBillingIdentity(body);
    savedAddress.value = { ...EMPTY_BILLING, ...view.address };
    Object.assign(address, savedAddress.value);
    billingComplete.value = view.complete;
  } catch (err) {
    billingError.value = err instanceof ApiError ? err.message : 'Could not save the address.';
  } finally {
    billingBusy.value = false;
  }
}

// ─── Password ────────────────────────────────────────────────────────────────

const PASSWORD_MIN = 8;
const current = ref('');
const next = ref('');
const repeat = ref('');
const passwordDone = ref(false);

const mismatch = computed(() => repeat.value.length > 0 && next.value !== repeat.value);
const canChangePassword = computed(
  () =>
    !account.busy.value &&
    current.value.length > 0 &&
    next.value.length >= PASSWORD_MIN &&
    next.value === repeat.value,
);

/**
 * Changing it ends every session, this one included — the server revokes the
 * refresh tokens and clears both cookies. So the right move afterwards is to sign
 * out locally: an app still believing it has a session 401s on its next request,
 * which reads as the change having broken something.
 *
 * The delay is so the confirmation is actually seen. Signing out at once would
 * swap the whole screen for the landing page, and a player who had just typed
 * three passwords would have no idea whether it worked.
 */
async function submitPassword(): Promise<void> {
  if (!canChangePassword.value) return;
  if (!(await account.changePassword(current.value, next.value))) return;
  passwordDone.value = true;
  current.value = '';
  next.value = '';
  repeat.value = '';
  window.setTimeout(() => session.logout(), 2200);
}

// ─── Danger zone ─────────────────────────────────────────────────────────────

const dangerOpen = ref(false);
const deletePassword = ref('');
const deleteEmail = ref('');

/**
 * Two independent proofs, and the typed address is compared case-insensitively
 * exactly as the server does. The disabled button is the courtesy; the server
 * checks both.
 */
const canDelete = computed(
  () =>
    !account.busy.value &&
    deletePassword.value.length > 0 &&
    deleteEmail.value.trim().toLowerCase() ===
      (account.profile.value?.email ?? '').toLowerCase(),
);

async function submitDelete(): Promise<void> {
  if (!canDelete.value) return;
  if (!(await account.deleteAccount(deletePassword.value, deleteEmail.value.trim()))) return;
  // There is nothing left to be signed into, so this is not a courtesy.
  emit('deleted');
  session.logout();
}

function onLocale(event: Event): void {
  account.savePreferences({ locale: (event.target as HTMLSelectElement).value });
}

function onCurrency(event: Event): void {
  account.savePreferences({ currency: (event.target as HTMLSelectElement).value });
}
</script>

<template>
  <div class="pane" data-test="pane-account">
    <section class="block identity">
      <h3>Identity</h3>

      <!--
        The card. A display of the three things a player is known by — rank, call
        sign, name — over the rows that edit them, which stay exactly what they
        were: the shape rule, the uniqueness refusal and the verification gate all
        live on the inputs below and are explained there once.
      -->
      <div class="player-card" data-test="profile-card">
        <span class="card-glare" aria-hidden="true"></span>

        <div class="card-rank">
          <div class="rank-disc" data-test="profile-card-rank">
            <img
              v-if="rankArt"
              class="rank-art"
              :src="rankArt"
              alt=""
              data-test="profile-card-rank-art"
              decoding="async"
              @error="rankArtMissing = true"
            />
            <span v-else class="rank-plate">{{ wallet?.rank ?? '—' }}</span>
          </div>
          <span class="rank-name" data-test="profile-card-rank-name">
            {{ wallet?.rankTitle ?? 'Unranked' }}
          </span>
        </div>

        <!--
          Two modes in one place. At rest the names are *text* — the card is
          finished, and a control on a correct name is a control nobody wants.
          `Edit card` turns exactly these lines into the inputs that write them,
          and Save sits where the button that opened it was.
        -->
        <div v-if="!editing" class="card-who">
          <span class="card-callsign" data-test="profile-card-callsign">
            {{ account.profile.value?.callSign || 'No call sign' }}
          </span>
          <!--
            **Falsy, not `??`.** An account that has had its name cleared holds
            `''` rather than null, and nullish-coalescing would let that through
            and render the line blank — a missing name that does not say it is
            missing, which is the one thing this line exists to do.
          -->
          <span
            class="card-fullname"
            :class="{ empty: !account.profile.value?.fullName }"
            data-test="profile-card-fullname"
          >
            {{ account.profile.value?.fullName || 'Name not available' }}
          </span>
          <button
            type="button"
            class="card-edit"
            data-test="profile-card-edit"
            :disabled="!cardEditable"
            :title="cardEditable ? 'Edit your call sign and name' : 'Confirm your email address first'"
            @click="beginEdit"
          >
            <!-- The pencil is drawn, not a glyph: a font emoji is a different
                 size and colour on every platform, and this one sits inside a
                 text button. -->
            <svg class="card-edit-icon" viewBox="0 0 16 16" aria-hidden="true">
              <path
                d="M11.6 1.6a1.4 1.4 0 0 1 2 2l-.9.9-2-2 .9-.9ZM9.8 3.4l2 2L5 12.2l-2.5.6.6-2.5 6.7-6.9Z"
                fill="currentColor"
              />
            </svg>
            Edit card
          </button>
        </div>

        <div v-else class="card-who editing">
          <input
            id="pf-callsign"
            v-model="callSign"
            class="card-input callsign"
            type="text"
            :minlength="CALL_SIGN_MIN"
            :maxlength="CALL_SIGN_MAX"
            autocomplete="off"
            spellcheck="false"
            data-test="profile-callsign"
            placeholder="Call sign"
            :disabled="account.busy.value"
          />
          <input
            id="pf-name"
            v-model="fullName"
            class="card-input fullname"
            type="text"
            maxlength="120"
            autocomplete="name"
            data-test="profile-fullname"
            placeholder="Full name"
            :disabled="account.busy.value"
          />
          <div class="card-actions">
            <button
              type="button"
              class="primary compact"
              data-test="profile-card-save"
              :disabled="!canSaveCard"
              @click="saveCard"
            >
              Save
            </button>
            <button
              type="button"
              class="ghost compact"
              data-test="profile-card-cancel"
              :disabled="account.busy.value"
              @click="cancelEdit"
            >
              Cancel
            </button>
          </div>
          <!--
            The call sign's three explanations, which moved up here with the
            input rather than being dropped. Shape is ours; uniqueness is the
            server's and is reported by the overlay's own error line, because a
            client that guessed at it would be wrong the moment somebody else
            took a name.
          -->
          <p
            v-if="callSignDirty && !callSignWellFormed"
            class="card-note warn"
            data-test="profile-callsign-shape"
          >
            Letters, digits, and . _ - inside it — starting and ending with a
            letter or digit, {{ CALL_SIGN_MIN }} to {{ CALL_SIGN_MAX }} characters.
          </p>
          <p v-else class="card-note" data-test="profile-card-hint">
            Your call sign is worn in battle and has to be unique. The full name
            is printed on your invoices, and is optional.
          </p>
        </div>
      </div>

      <label>Email</label>
      <p class="hint">
        This is how you sign in and how the account is recovered, so it cannot be
        changed here.
      </p>
      <div class="readonly" data-test="profile-email">
        <span class="value">{{ account.profile.value?.email ?? '—' }}</span>
        <span v-if="account.verified.value" class="badge ok" data-test="profile-verified">
          Confirmed
        </span>
        <span v-else class="badge bad" data-test="profile-unverified">Not confirmed</span>
      </div>
      <button
        v-if="!account.verified.value"
        type="button"
        class="ghost"
        data-test="profile-resend"
        :disabled="account.busy.value"
        @click="account.resendVerification()"
      >
        Send the confirmation email again
      </button>

      <label>Enlisted</label>
      <!-- Stated, not boxed. Nothing about this one is editable or ever will be,
           and a plate that looks like a disabled input invites the try. -->
      <p class="stated" data-test="profile-enlisted">
        {{ formatDate(account.profile.value?.createdAt) }}
      </p>
      <!--
        The two name rows that used to stand here are gone: the card is the
        form now. What had to come with them is the verification gate, which is
        a fact about the account rather than about either field — see
        `AccountService.updateFullName`. The row a name writes to is created by
        the garage's first read, and the garage is behind the gate, so there is
        nothing to rename until the address is confirmed. Saying so beats
        offering an edit that 404s, which is why `Edit card` is disabled rather
        than hidden.
      -->
      <p v-if="!cardEditable" class="hint warn" data-test="profile-card-locked">
        Confirm your email address first. Use the link we sent you, then come
        back and your call sign and name become editable.
      </p>

    </section>

    <section class="block prefs">
      <h3>Preferences</h3>

      <label for="pf-locale">Language</label>
      <p class="hint">
        Used for the emails we send you. The interface is English throughout.
      </p>
      <select
        id="pf-locale"
        data-test="profile-locale"
        :value="account.preferences.value?.locale ?? account.config.value?.defaultLocale ?? ''"
        :disabled="account.busy.value || !account.config.value"
        @change="onLocale"
      >
        <option v-for="l in account.config.value?.locales ?? []" :key="l.code" :value="l.code">
          {{ l.label }}
        </option>
      </select>

      <label for="pf-currency">Currency</label>
      <p class="hint">
        What real-money prices are quoted and charged in. Diamonds, credits and
        fuel are in-game and are not affected.
      </p>
      <select
        id="pf-currency"
        data-test="profile-currency"
        :value="
          account.preferences.value?.currency ?? account.config.value?.defaultCurrency ?? ''
        "
        :disabled="account.busy.value || !account.config.value"
        @change="onCurrency"
      >
        <option v-for="c in account.config.value?.currencies ?? []" :key="c.code" :value="c.code">
          {{ c.code }} &mdash; {{ c.symbol }}
        </option>
      </select>
      <p
        v-if="(account.config.value?.currencies.length ?? 0) < 2"
        class="hint"
        data-test="profile-currency-single"
      >
        This server quotes prices in one currency only, so there is nothing to
        switch to.
      </p>
    </section>

    <section class="block billing">
      <h3>Billing address</h3>
      <p class="hint">
        Printed on your invoices. Required for Apple Pay and Open Banking; optional
        for card and BLIK — once it is complete it is sent with every payment.
        <strong v-if="billingComplete" data-test="billing-complete">Complete.</strong>
      </p>

      <div class="fields">
        <div class="field">
          <label for="pf-first">First name</label>
          <input id="pf-first" v-model="address.firstName" type="text" maxlength="64" autocomplete="given-name"
            data-test="profile-billing-first-name" :disabled="billingBusy" />
        </div>
        <div class="field">
          <label for="pf-last">Last name</label>
          <input id="pf-last" v-model="address.lastName" type="text" maxlength="64" autocomplete="family-name"
            data-test="profile-billing-last-name" :disabled="billingBusy" />
        </div>

        <div class="field wide">
          <label for="pf-addr1">Address</label>
          <input id="pf-addr1" v-model="address.line1" type="text" maxlength="50" autocomplete="address-line1"
            data-test="profile-address-line1" placeholder="Street and number" :disabled="billingBusy" />
          <small v-if="address.line1 && billingErrors.line1" class="field-error">{{ billingErrors.line1 }}</small>
        </div>

        <div class="field wide">
          <label for="pf-addr2">Address, continued</label>
          <input id="pf-addr2" v-model="address.line2" type="text" maxlength="50" autocomplete="address-line2"
            data-test="profile-address-line2" placeholder="Flat, floor — if you need it" :disabled="billingBusy" />
        </div>

        <div class="field">
          <label for="pf-postcode">Postcode</label>
          <input id="pf-postcode" v-model="address.zip" type="text" maxlength="10" autocomplete="postal-code"
            data-test="profile-address-postcode" :disabled="billingBusy" />
          <small v-if="address.zip && billingErrors.zip" class="field-error">{{ billingErrors.zip }}</small>
        </div>

        <div class="field">
          <label for="pf-city">City</label>
          <input id="pf-city" v-model="address.city" type="text" maxlength="50" autocomplete="address-level2"
            data-test="profile-address-city" :disabled="billingBusy" />
          <small v-if="address.city && billingErrors.city" class="field-error">{{ billingErrors.city }}</small>
        </div>

        <div v-if="needsState" class="field">
          <label for="pf-state">State / province</label>
          <input id="pf-state" v-model="address.state" type="text" maxlength="3" autocomplete="address-level1"
            data-test="profile-address-state" :disabled="billingBusy" />
          <small v-if="billingErrors.state" class="field-error">{{ billingErrors.state }}</small>
        </div>

        <div class="field">
          <label for="pf-country">Country</label>
          <select id="pf-country" v-model="address.country" data-test="profile-address-country" :disabled="billingBusy">
            <option :value="null">—</option>
            <option v-for="c in COUNTRIES" :key="c.code" :value="c.code">{{ c.name }}</option>
          </select>
        </div>
      </div>

      <p v-if="billingError" class="status error" data-test="billing-error">{{ billingError }}</p>
      <button
        type="button"
        class="primary"
        data-test="profile-address-save"
        :disabled="!addressDirty || billingBusy"
        @click="saveBilling"
      >
        Save address
      </button>
    </section>

    <!-- The three inputs are wrapped a field each so that this block, which is
         full width once there is room for two columns, can lay them out three
         across rather than stacking three 1,000px password boxes. See the
         container query in this file's stylesheet. -->
    <section class="block password">
      <h3>Password</h3>
      <p class="hint">
        Changing it signs out every device, including this one, so you will be
        asked to sign in again.
      </p>

      <p v-if="passwordDone" class="ok-note" data-test="profile-password-done">
        Password changed. Signing you out.
      </p>
      <template v-else>
        <div class="fields">
          <div class="field">
            <label for="pf-current">Current password</label>
            <input
              id="pf-current"
              v-model="current"
              type="password"
              autocomplete="current-password"
              data-test="profile-current-password"
              :disabled="account.busy.value"
            />
          </div>

          <div class="field">
            <label for="pf-next">New password</label>
            <input
              id="pf-next"
              v-model="next"
              type="password"
              autocomplete="new-password"
              data-test="profile-new-password"
              :placeholder="`At least ${PASSWORD_MIN} characters`"
              :disabled="account.busy.value"
            />
          </div>

          <div class="field">
            <label for="pf-repeat">Repeat the new password</label>
            <input
              id="pf-repeat"
              v-model="repeat"
              type="password"
              autocomplete="new-password"
              data-test="profile-repeat-password"
              :disabled="account.busy.value"
            />
          </div>
        </div>
        <p v-if="mismatch" class="warn-note" data-test="profile-password-mismatch">
          Those two do not match.
        </p>

        <button
          type="button"
          class="primary"
          data-test="profile-password-save"
          :disabled="!canChangePassword"
          @click="submitPassword"
        >
          Change password
        </button>
      </template>
    </section>

    <section class="block danger">
      <h3>Danger zone</h3>

      <template v-if="!dangerOpen">
        <p class="hint">
          Deleting your account removes it and everything on it: the garage and
          every hull, turret, part and livery in it, your research, your service
          record and your balances. It cannot be undone.
        </p>
        <button
          type="button"
          class="danger-btn"
          data-test="profile-delete-open"
          @click="dangerOpen = true"
        >
          Delete my account
        </button>
      </template>

      <template v-else>
        <p class="hint">
          This is permanent. Paid invoices are kept as accounting records with your
          name and address removed, because tax law requires it. Nothing else
          survives.
        </p>

        <div class="fields">
          <div class="field">
            <label for="pf-del-password">Your password</label>
            <input
              id="pf-del-password"
              v-model="deletePassword"
              type="password"
              autocomplete="current-password"
              data-test="profile-delete-password"
              :disabled="account.busy.value"
            />
          </div>

          <div class="field">
            <label for="pf-del-email">Type your email address to confirm</label>
            <input
              id="pf-del-email"
              v-model="deleteEmail"
              type="email"
              autocomplete="off"
              data-test="profile-delete-email"
              :placeholder="account.profile.value?.email ?? ''"
              :disabled="account.busy.value"
            />
          </div>
        </div>

        <div class="row buttons">
          <!-- The one control in the profile screen that is both a commit and a
               destruction, so it takes the *commit* shape (a solid plate) in the
               danger palette. The opener above it stays an outline: opening the
               danger zone destroys nothing. -->
          <button
            type="button"
            class="danger-btn commit"
            data-test="profile-delete-confirm"
            :disabled="!canDelete"
            @click="submitDelete"
          >
            {{ account.busy.value ? 'Deleting…' : 'Delete my account for ever' }}
          </button>
          <button
            type="button"
            class="ghost"
            data-test="profile-delete-cancel"
            @click="dangerOpen = false"
          >
            Keep my account
          </button>
        </div>
      </template>
    </section>
  </div>
</template>

<style scoped>
@import './pane.css';

/*
 * ─── Two up, once there is room for two ──────────────────────────────────────
 *
 * This is the pane that made the width cap safe to raise. Billing and Purchases
 * are one block each and a wide table is a *better* table; a form is not — a
 * text input 900px wide is a worse text input than one 430px wide, so a single
 * column of blocks in a capped-and-centred frame would have traded a dead zone
 * beside the document for a dead zone inside it. Four blocks over two columns
 * fill the pane at a measure a field actually wants, which is the storefront's
 * own rule ("no empty space inside the pane; scale the composition rather than
 * re-flow it") applied honestly to a form.
 *
 * **A container query, not a media query.** The rail and the frame's cap both
 * stand between the viewport and this pane, so the viewport cannot answer how
 * much room these blocks have — and the answer differs by 10rem depending on
 * whether the rail is a column or a strip at the same window width. The
 * container is `.content` in `ProfileOverlay`, named so a container added inside
 * a pane later cannot silently capture this.
 *
 * 42rem is two 20-and-a-bit-rem columns, which is where a labelled field with a
 * hint under it stops being cramped. Measured: a 1,440px laptop gives the pane
 * ~55rem, so two ~27rem columns; the frame's 76rem ceiling gives two ~31rem.
 *
 * **The danger zone is last and spans the pane**, which is a correction of what
 * shipped: a tidy 2×2 put it in the bottom-right cell, beside Password, where
 * *tidy* was the only argument for it. Deleting an account is the end of the
 * document — it belongs under everything, at the width of everything, so that
 * scrolling to the bottom is what finds it and nothing sits below it to be
 * mistaken for the next thing to do.
 *
 * Spanning it would have stranded Password in a half-width row with a block-sized
 * hole beside it, which is the "no empty space inside the pane" rule broken to
 * satisfy the same rule elsewhere. So Password spans too, and both full-width
 * blocks lay their own inputs out in columns (below) — a form field is bounded by
 * the field grid rather than by the block, so a block may be as wide as it likes.
 *
 * Identity | Preferences on row one, Password across row two, danger across row
 * three: every row full, nothing stranded, and the destructive thing last.
 */
@container profile-pane (min-width: 42rem) {
  .pane {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    align-items: start;
  }

  .billing,
  .password,
  .danger {
    grid-column: 1 / -1;
  }

  /*
   * Postcode | City | Country across a row, with the two street lines spanning
   * above them. That is the shape of an address rather than the shape of the
   * grid: the street is long and the three below it are short, and a postcode
   * given the same width as a street line reads as a field somebody mis-sized.
   */
  .billing .fields {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 0.8rem 1rem;
  }

  .billing .field.wide {
    grid-column: 1 / -1;
  }

  /* Current | New | Repeat, which is also the order they are filled in. */
  .password .fields {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 0.8rem 1rem;
  }

  /* The two proofs, side by side — they are independent of each other, and
     reading them as a pair is the point (see `canDelete`). */
  .danger .fields {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 0.8rem 1rem;
  }
}

/*
 * ─── The two dropdowns stop at the width of their own longest option ─────────
 *
 * `select` inherits nothing that bounds it, so both of these grew to the block —
 * roughly 27rem in the two-column layout and the full pane below it — to hold
 * "English (United Kingdom)" and "EUR — €". A control whose content is a dozen
 * characters and whose hit area is a third of the screen reads as a text field
 * somebody forgot to style, and the chevron ends up marooned an inch from the
 * word it belongs to.
 *
 * `max-width` rather than `width`: below the container query these blocks are
 * the width of a phone, and a fixed 22rem would be the one element on the pane
 * with a horizontal scrollbar. Currency is narrower because its options are a
 * three-letter code and a symbol, and nothing longer can ever appear in it —
 * `CURRENCIES` is validated as ISO codes.
 */
.prefs select,
.billing .field-error {
  color: #ff8a7a;
  font-size: 0.8rem;
}

.billing select {
  max-width: 22rem;
}

.prefs select#pf-currency {
  max-width: 12rem;
}

/*
 * A field is a label and its input as one unit, which is what lets the two
 * full-width blocks above put three or two of them across a row. Below the
 * breakpoint it is an ordinary stack and the gap does the spacing `pane.css`'s
 * `input + label` rule does elsewhere — the two never both apply, because
 * wrapping the inputs is exactly what stops them being siblings.
 */
.fields {
  display: flex;
  flex-direction: column;
  gap: 0.8rem;
  margin-bottom: 0.2rem;
}

.field {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.field label {
  margin-top: 0;
}

/*
 * The danger zone is the same plate, tinted — not a different kind of object.
 * The red is carried by the border, the heading and its rule; the block itself
 * stays a block, because turning it into a warning banner would make the
 * *unopened* state (which is only a description and a button) shout louder than
 * the account it belongs to.
 */
.danger {
  border-color: rgba(217, 72, 59, 0.42);
  background: linear-gradient(180deg, rgba(217, 72, 59, 0.1) 0%, var(--steel-800) 48%);
}

.danger h3 {
  border-bottom-color: rgba(217, 72, 59, 0.38);
  color: #ff8478;
}

.buttons {
  margin-top: 0.85rem;
  align-items: center;
}

.buttons .ghost {
  margin-top: 0;
}

/*
 * The danger palette wearing the game's two button shapes rather than a third of
 * its own — `.ghost`'s outline for the control that only opens the zone, and
 * `.primary`'s solid plate (`.commit`, below) for the one that actually deletes.
 * The geometry is identical to `pane.css`'s pair, so the only thing that differs
 * across the whole screen is the hue.
 */
.danger-btn {
  align-self: flex-start;
  margin-top: 0.7rem;
  padding: 0.5rem 1rem;
  border: 1px solid rgba(217, 72, 59, 0.75);
  border-radius: 3px;
  background: transparent;
  color: #ff8478;
  font-size: 0.82rem;
  font-weight: 700;
  cursor: pointer;
  transition: background 120ms ease, border-color 120ms ease, color 120ms ease,
    filter 120ms ease, transform 80ms ease;
}

.buttons .danger-btn {
  margin-top: 0;
}

.danger-btn:hover:not(:disabled) {
  border-color: #d9483b;
  background: rgba(217, 72, 59, 0.18);
  color: #fff;
}

/* Solid, because this one is the act. `.primary`'s exact geometry and press. */
.danger-btn.commit {
  padding: 0.5rem 1.3rem;
  border-color: #d9483b;
  background: #d9483b;
  color: #fff;
}

.danger-btn.commit:hover:not(:disabled) {
  background: #d9483b;
  filter: brightness(1.1);
}

.danger-btn:active:not(:disabled) {
  transform: translateY(1px);
}

.danger-btn:focus-visible {
  outline: 2px solid #ff8478;
  outline-offset: 2px;
}

.danger-btn:disabled {
  opacity: 0.45;
  cursor: default;
}

@media (prefers-reduced-motion: reduce) {
  .danger-btn {
    transition: none;
  }

  .danger-btn:active:not(:disabled) {
    transform: none;
  }
}

@media (hover: none) and (pointer: coarse) {
  .danger-btn {
    min-height: 44px;
  }
}

/* ─── The player card ────────────────────────────────────────────────────── */

/*
 * The one thing on this screen that is not a form.
 *
 * Everything else in the pane is a steel plate with a hairline, because
 * everything else is a row somebody edits. This is the account as the game shows
 * it — rank, call sign, name — so it takes the accent and the lift the shop's
 * cards take, and it is the only orange-bordered element here for exactly that
 * reason: a second one would make the first mean nothing.
 */
.player-card {
  position: relative;
  overflow: hidden;
  display: grid;
  grid-template-columns: auto 1fr;
  align-items: center;
  gap: 1.25rem;
  margin: 0 0 1.35rem;
  padding: 1.15rem 1.35rem;
  border: 1px solid var(--accent);
  border-radius: 8px;
  /*
   * Warm at the badge and cold away from it, so the card has a light source
   * rather than a gradient. The inner hairline is the accent at a tenth: a card
   * lit from one corner needs an edge on the dark side or it reads as unfinished.
   */
  background:
    radial-gradient(115% 150% at 10% 0%, rgba(232, 114, 44, 0.26), transparent 62%),
    linear-gradient(180deg, #221507 0%, #15110d 48%, var(--steel-800) 100%);
  box-shadow:
    inset 0 0 0 1px rgba(232, 114, 44, 0.12),
    0 10px 26px rgba(0, 0, 0, 0.45);
}

/*
 * The glare. A single pass every six seconds, not a shimmer.
 *
 * It rests for most of the cycle and crosses in the last quarter, which is what
 * makes it read as a card catching the light rather than as something loading.
 * `inset` overshoots on both axes so the bar is already off-card at each end and
 * never appears out of nowhere at the border.
 */
.card-glare {
  position: absolute;
  inset: -40% -60%;
  pointer-events: none;
  background: linear-gradient(
    105deg,
    transparent 42%,
    rgba(255, 255, 255, 0.14) 50%,
    transparent 58%
  );
  transform: translateX(-60%);
  animation: card-sweep 6s cubic-bezier(0.4, 0, 0.2, 1) infinite;
}

@keyframes card-sweep {
  0%,
  74% {
    transform: translateX(-60%);
  }
  100% {
    transform: translateX(60%);
  }
}

/*
 * A read-only value that is not a field.
 *
 * `.readonly` draws a plate with a border, which is right for the email — that
 * one is a field somebody would otherwise expect to edit, and the plate is what
 * says "this is the value, and it is fixed". The enlistment date is a fact about
 * the account, closer to a caption than to a form control.
 */
.stated {
  margin: 0;
  color: var(--steel-200);
  font-size: 0.86rem;
}

/* The step `.readonly` and `.hint` already give the label after them. */
.stated + label {
  margin-top: 1.05rem;
}

.card-rank {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.55rem;
}

/*
 * The disc is the frame and the insignia is bigger than it.
 *
 * `overflow: visible` is the whole effect: the art breaks the ring at the top and
 * the sides, which is what gives a flat badge some relief without a shadow pass.
 * The ring is drawn under it, so what the eye reads is one object standing in a
 * hoop rather than a picture cropped into a circle.
 */
.rank-disc {
  position: relative;
  width: 88px;
  height: 88px;
  border-radius: 50%;
  border: 2px solid var(--accent);
  background: radial-gradient(circle at 50% 40%, #2a1a0c 0%, #0d1014 78%);
  box-shadow: 0 0 18px rgba(232, 114, 44, 0.22);
}

.rank-art {
  position: absolute;
  left: 50%;
  top: 50%;
  width: 116px;
  transform: translate(-50%, -52%);
  filter: drop-shadow(0 4px 8px rgba(0, 0, 0, 0.55));
  transition: transform 200ms cubic-bezier(0.2, 0.7, 0.3, 1);
}

/* The rank's number, for a rank whose art is not on disk. See `rankArtMissing`. */
.rank-plate {
  position: absolute;
  inset: 0;
  display: grid;
  place-items: center;
  font-family: var(--font-display);
  font-size: 1.9rem;
  color: var(--accent);
}

.player-card:hover .rank-art {
  transform: translate(-50%, -57%) scale(1.05);
}

.rank-name {
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.09em;
  text-transform: uppercase;
  color: var(--accent);
}

.card-who {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0.15rem;
  min-width: 0;
}

/*
 * At rest both lines are plain text, and that is the point.
 *
 * They were buttons that focused the rows below, and the card was covered in
 * affordances for an edit almost nobody is making — a name is typed once. The
 * edit is one control now, so these two carry no hover, no cursor and no
 * outline: nothing on the card moves until `Edit card` is pressed.
 */
.card-callsign,
.card-fullname {
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.card-callsign {
  font-family: var(--font-display);
  font-size: 1.55rem;
  line-height: 1.15;
  color: #f4f7fa;
}

.card-fullname {
  font-size: 0.9rem;
  color: var(--steel-200);
}

/*
 * The placeholder tone: thin and quiet, so *Name not available* reads as the
 * absence of a value rather than as a value. It used to be the accent colour,
 * which is what the card uses for its rank name — that made a missing name the
 * brightest text on the line and it read as a warning about nothing.
 */
.card-fullname.empty {
  font-weight: 300;
  font-style: italic;
  color: var(--steel-400);
}

/* The one control on the card at rest. Quiet until pointed at, because it is
   offering an edit rather than asking for one. */
.card-edit {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  margin: 0.45rem 0 0;
  padding: 0.26rem 0.6rem;
  border: 1px solid rgba(232, 114, 44, 0.42);
  border-radius: 4px;
  background: rgba(232, 114, 44, 0.08);
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.07em;
  text-transform: uppercase;
  color: var(--accent);
  cursor: pointer;
  transition: background 120ms ease, border-color 120ms ease, color 120ms ease;
}

.card-edit:hover:not(:disabled) {
  border-color: var(--accent);
  background: rgba(232, 114, 44, 0.2);
  color: #fff;
}

.card-edit:focus-visible {
  outline: 2px solid var(--accent);
  outline-offset: 2px;
}

.card-edit:disabled {
  border-color: rgba(255, 255, 255, 0.14);
  background: none;
  color: var(--steel-400);
  cursor: not-allowed;
}

.card-edit-icon {
  width: 0.85em;
  height: 0.85em;
  flex: none;
}

/* Edit mode. The inputs stand where the text stood, at the same sizes, so the
   card does not resize when it becomes a form. */
.card-who.editing {
  align-self: stretch;
  gap: 0.4rem;
}

.card-input {
  width: 100%;
  max-width: 22rem;
  padding: 0.2rem 0.45rem;
  border: 1px solid rgba(255, 255, 255, 0.22);
  border-radius: 4px;
  background: rgba(0, 0, 0, 0.34);
  color: #f4f7fa;
}

.card-input:focus {
  border-color: var(--accent);
  outline: none;
}

.card-input.callsign {
  font-family: var(--font-display);
  font-size: 1.35rem;
  line-height: 1.2;
}

.card-input.fullname {
  font-size: 0.9rem;
}

.card-actions {
  display: flex;
  gap: 0.5rem;
  margin-top: 0.15rem;
}

/* Wraps, unlike the two name lines: this is a sentence, and clipping it to one
   ellipsised row would hide the half that says what is wrong. */
.card-note {
  margin: 0.1rem 0 0;
  max-width: 26rem;
  font-size: 0.72rem;
  line-height: 1.35;
  color: var(--steel-300);
  white-space: normal;
}

.card-note.warn {
  color: #f0b357;
}

/* One column, and the badge over the names rather than beside them. */
@media (max-width: 30rem) {
  .player-card {
    grid-template-columns: 1fr;
    justify-items: center;
    text-align: center;
  }

  .card-who {
    align-items: center;
  }
}

@media (prefers-reduced-motion: reduce) {
  .card-glare {
    animation: none;
    opacity: 0;
  }

  .rank-art,
  .player-card:hover .rank-art {
    transition: none;
    transform: translate(-50%, -52%);
  }
}

/* No hover to reveal them with, so the two lines carry a permanent hairline. */
@media (hover: none) and (pointer: coarse) {
  .card-callsign,
  .card-fullname {
    min-height: 44px;
    display: flex;
    align-items: center;
    box-shadow: inset 0 -1px 0 rgba(232, 114, 44, 0.35);
  }
}
</style>
